import React, { useMemo } from 'react';

interface ConfettiProps {
  count?: number;
  colors?: string[];
}

/**
 * Lightweight CSS-only confetti burst. One-shot — uses `forwards` fill so it
 * doesn't loop. Mount once on game-end, unmount when leaving the screen.
 */
export function Confetti({ count = 60, colors = ['#FF5A1F', '#16A34A', '#F59E0B', '#3B6FE0', '#8B3DC4', '#FFD86E'] }: ConfettiProps) {
  const pieces = useMemo(() => {
    return Array.from({ length: count }).map((_, i) => {
      const delay = Math.random() * 1.2;
      const left = Math.random() * 100;
      const dur = 2.4 + Math.random() * 1.6;
      const color = colors[i % colors.length];
      const w = 7 + Math.random() * 4;
      const h = 11 + Math.random() * 7;
      const rot = Math.random() * 360;
      const drift = (Math.random() - 0.5) * 80;
      return { id: i, delay, left, dur, color, w, h, rot, drift, round: i % 3 === 0 };
    });
  }, [count, colors]);

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 1,
      }}
    >
      {pieces.map(p => (
        <div
          key={p.id}
          className="confetti-piece"
          style={
            {
              top: -20,
              left: `${p.left}%`,
              width: p.w,
              height: p.h,
              background: p.color,
              borderRadius: p.round ? '50%' : 2,
              transform: `rotate(${p.rot}deg)`,
              opacity: 0.92,
              ['--dur' as any]: `${p.dur}s`,
              ['--delay' as any]: `${p.delay}s`,
              ['--drift' as any]: `${p.drift}px`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}
